import React, { useMemo, useCallback } from 'react';
import { Carousel } from 'antd';
import type { CarouselProps as AntCarouselProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { createCommand } from '../../base/actionParser';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

// 旧值到新值的映射
const DOT_POSITION_TO_PLACEMENT: Record<string, string> = {
  'top': 'top',
  'bottom': 'bottom',
  'left': 'start',
  'right': 'end',
};

// Slide 配置项类型
export interface SlideItem {
  title: string;  // 显示标题
  key: string;    // 唯一标识
}

export interface AndCarouselProps extends Omit<AntCarouselProps, 'dotPosition' | 'afterChange'>, BaseComponentProps, DataSetConfig {
  /** @deprecated 使用 dotPlacement 替代 */
  dotPosition?: 'top' | 'bottom' | 'left' | 'right';
  /** 指示点位置 */
  dotPlacement?: 'start' | 'end' | 'top' | 'bottom';
  /** Slide 配置列表 */
  slides?: SlideItem[] | string;
  /** 变更动作 */
  changeAction?: string;
  /** onChange 回调 */
  afterChange?: (current: number) => void;
  /** 开发模式 */
  develop?: boolean;
  /** Slide 内容渲染函数 */
  renderSlideContent?: (key: string, index: number) => React.ReactNode;
}

// 解析 slides 配置
const parseSlides = (slides: SlideItem[] | string | undefined): SlideItem[] => {
  if (!slides) return [];
  if (typeof slides === 'string') {
    try {
      return JSON.parse(slides);
    } catch {
      return [];
    }
  }
  return slides;
};

export const AndCarousel: React.FC<AndCarouselProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  dotPosition,
  dotPlacement,
  slides,
  changeAction,
  afterChange,
  renderSlideContent,
  children,
  ...restProps
}) => {
  const addCommand = useAndStore((state) => state.addCommand);
  const setDataSet = useAndStore((state) => state.setDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  // 将旧的 dotPosition 映射到新的 dotPlacement
  const computedDotPlacement = useMemo(() => {
    if (dotPlacement) return dotPlacement;
    if (dotPosition) {
      return DOT_POSITION_TO_PLACEMENT[dotPosition] || dotPosition;
    }
    return undefined;
  }, [dotPosition, dotPlacement]);

  // 解析 slides 配置
  const parsedSlides = useMemo(() => parseSlides(slides), [slides]);



  // 处理 slide 切换
  const handleAfterChange = useCallback((current: number) => {
    if (afterChange) {
      afterChange(current);
      return;
    }
    if (dataSetKey && dataSetField) {
      setDataSet(dataSetKey, dataSetField, current);
    }
    if (changeAction && sessionId) {
      addCommand(sessionId, createCommand(changeAction, current));
    }
  }, [afterChange, dataSetKey, dataSetField, setDataSet, changeAction, sessionId, addCommand]);

  // 生成 slide 内容
  const slideContents = useMemo(() => {
    // 如果有 children，直接使用 children
    if (children) {
      return children;
    }

    // 如果有 slides 配置，渲染每个 slide
    if (parsedSlides.length > 0) {
      return parsedSlides.map((slide, index) => {
        const content = renderSlideContent ? renderSlideContent(slide.key, index) : null;
        return (
          <div key={slide.key} data-slide-key={slide.key}>
            {content || (
              <div style={{ 
                minHeight: '100px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: '#999',
                background: develop ? '#f5f5f5' : undefined,
              }}>
                {develop ? slide.title : null}
              </div>
            )}
          </div>
        );
      });
    }

    return null;
  }, [children, parsedSlides, renderSlideContent, develop]);

  return (
    <Carousel
      {...restProps}
      dotPlacement={computedDotPlacement as any}
      afterChange={handleAfterChange}
    >
      {slideContents}
    </Carousel>
  );
};

export default AndCarousel;
